import type { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import type { ScenarioInput, ScenarioAnalysis, ComparisonMetrics, MortgagePayment } from '@/types/supremeIntelligenceTypes';

/**
 * Calculate comprehensive analysis for a mortgage scenario
 */
export const calculateScenarioAnalysis = (
  scenario: ScenarioInput,
  formData?: EnhancedUnifiedFormData
): ScenarioAnalysis => {
  const loanAmount = scenario.homePrice * (1 - scenario.downPaymentPercentage / 100);
  const monthlyRate = scenario.interestRate / 100 / 12;
  const numPayments = scenario.loanTerm * 12;

  // Calculate monthly principal & interest
  const monthlyPI = loanAmount > 0 
    ? (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
      (Math.pow(1 + monthlyRate, numPayments) - 1)
    : 0;

  // Calculate other monthly costs
  const monthlyTaxes = (scenario.homePrice * (scenario.propertyTaxRate / 100)) / 12;
  const monthlyInsurance = scenario.homeInsurance / 12;
  
  // PMI calculation (if down payment < 20%)
  const monthlyPMI = scenario.downPaymentPercentage < 20 
    ? loanAmount * 0.005 / 12 // 0.5% annual PMI
    : 0;

  const monthlyPayment: MortgagePayment = {
    principal: monthlyPI * 0.2, // Approximate first month
    interest: monthlyPI * 0.8,
    taxes: monthlyTaxes,
    insurance: monthlyInsurance,
    pmi: monthlyPMI,
    hoa: scenario.hoaFees,
    total: monthlyPI + monthlyTaxes + monthlyInsurance + monthlyPMI + scenario.hoaFees,
  };

  // Calculate 30-year costs
  const totalInterest = (monthlyPI * numPayments) - loanAmount;
  const totalCost30Year = (monthlyPayment.total * numPayments) + (scenario.homePrice * scenario.downPaymentPercentage / 100);

  // Calculate DTI ratio if formData available
  let dtiRatio = 0;
  if (formData) {
    const monthlyIncome = (formData.primaryIncome.annualIncome + formData.primaryIncome.additionalIncome) / 12;
    const coBorrowerIncome = formData.coBorrowerIncome.enabled ? formData.coBorrowerIncome.additionalIncome / 12 : 0;
    const totalMonthlyIncome = monthlyIncome + coBorrowerIncome;

    const monthlyDebts = 
      formData.debts.autoLoanPayment +
      formData.debts.creditCardPayment +
      formData.debts.personalLoanPayment +
      formData.debts.childSupportPayment +
      formData.debts.otherDebts;

    dtiRatio = totalMonthlyIncome > 0 ? ((monthlyDebts + monthlyPayment.total) / totalMonthlyIncome) * 100 : 0;
  }

  // Calculate affordability score
  const affordabilityScore = calculateAffordabilityScore({
    dtiRatio,
    downPaymentPercentage: scenario.downPaymentPercentage,
    monthlyPayment: monthlyPayment.total,
    homePrice: scenario.homePrice,
  });

  return {
    id: scenario.id,
    input: scenario,
    monthlyPayment,
    totalCost30Year,
    totalInterest,
    affordabilityScore,
    dtiRatio,
    downPaymentAmount: scenario.homePrice * (scenario.downPaymentPercentage / 100),
    loanAmount,
  };
};

/**
 * Compare multiple scenarios and identify best options
 */
export const compareScenarios = (scenarios: ScenarioAnalysis[]): ComparisonMetrics => {
  if (scenarios.length === 0) {
    return {
      bestMonthlyPayment: '',
      bestTotalCost: '',
      bestAffordability: '',
      lowestDTI: '',
    };
  }

  const bestMonthlyPayment = scenarios.reduce((best, current) => 
    current.monthlyPayment.total < best.monthlyPayment.total ? current : best
  ).id;

  const bestTotalCost = scenarios.reduce((best, current) => 
    current.totalCost30Year < best.totalCost30Year ? current : best
  ).id;

  const bestAffordability = scenarios.reduce((best, current) => 
    current.affordabilityScore > best.affordabilityScore ? current : best
  ).id;

  const lowestDTI = scenarios.reduce((best, current) => {
    if (current.dtiRatio === 0) return best;
    if (best.dtiRatio === 0) return current;
    return current.dtiRatio < best.dtiRatio ? current : best;
  }).id;

  return {
    bestMonthlyPayment,
    bestTotalCost,
    bestAffordability,
    lowestDTI,
  };
};

/**
 * Calculate affordability score (0-100)
 */
const calculateAffordabilityScore = (params: {
  dtiRatio: number;
  downPaymentPercentage: number;
  monthlyPayment: number;
  homePrice: number;
}): number => {
  let score = 50; // Base score

  // DTI component (0-30 points)
  if (params.dtiRatio === 0) {
    score += 15; // Neutral if no DTI data
  } else if (params.dtiRatio < 28) {
    score += 30;
  } else if (params.dtiRatio < 36) {
    score += 20;
  } else if (params.dtiRatio < 43) {
    score += 10;
  } // else 0 points

  // Down payment component (0-30 points)
  if (params.downPaymentPercentage >= 20) {
    score += 30;
  } else if (params.downPaymentPercentage >= 15) {
    score += 20;
  } else if (params.downPaymentPercentage >= 10) {
    score += 10;
  } else if (params.downPaymentPercentage >= 5) {
    score += 5;
  } // else 0 points

  // Payment-to-price ratio component (0-20 points)
  const paymentToPriceRatio = (params.monthlyPayment * 12 * 30) / params.homePrice;
  if (paymentToPriceRatio < 1.5) {
    score += 20;
  } else if (paymentToPriceRatio < 2.0) {
    score += 15;
  } else if (paymentToPriceRatio < 2.5) {
    score += 10;
  } else if (paymentToPriceRatio < 3.0) {
    score += 5;
  } // else 0 points

  return Math.min(100, Math.max(0, Math.round(score)));
};

/**
 * Create a default scenario from form data
 */
export const createScenarioFromFormData = (
  formData: EnhancedUnifiedFormData,
  name: string = 'Current Scenario'
): ScenarioInput => {
  return {
    id: crypto.randomUUID(),
    name,
    homePrice: formData.propertyLoan.homePrice,
    downPaymentPercentage: formData.propertyLoan.downPaymentPercentage,
    interestRate: formData.propertyLoan.interestRate,
    loanTerm: formData.propertyLoan.loanTerm,
    propertyTaxRate: formData.propertyLoan.propertyTaxRate,
    homeInsurance: formData.propertyLoan.homeInsurance,
    hoaFees: formData.propertyLoan.hoaFees,
  };
};
